/**
 * Title, description and indexing rule for every app route. Imported by the app (lib/meta.ts) to
 * update the head on navigation, and by the build (seo/pages.ts) to prerender one HTML file per
 * route, so both always agree. Kept free of page bodies so the app bundle stays small.
 */
import { GAME_NAME, GROUP_MAX_MEMBERS, MAX_TOTAL, SCORING } from '@geosport/shared';

export interface RouteMeta {
  path: string;
  title: string;
  description: string;
  /** Keep out of search results (personal or in-game screens). */
  noindex?: boolean;
}

const HOME_DESCRIPTION =
  'Five famous sports moments a day. Pin where each one happened on a 3D globe, guess the year, and compare scores with friends. Free, no sign-up.';

export const ROUTES: RouteMeta[] = [
  {
    path: '/',
    title: `${GAME_NAME} – Daily Sports Geography Game: Where & When?`,
    description: HOME_DESCRIPTION,
  },
  {
    path: '/how-to-play',
    title: `How to Play ${GAME_NAME}: Rules and Scoring`,
    description: `How ${GAME_NAME} works: pin where a sports moment happened (${SCORING.wherePoints} points) and guess the year (${SCORING.whenPoints} points). Later questions count more; a perfect day is ${MAX_TOTAL.toLocaleString('en-GB')}.`,
  },
  {
    path: '/leaderboard',
    title: `Daily Leaderboard · ${GAME_NAME}`,
    description: `Today's top ${GAME_NAME} scores, with your own rank, plus the last 14 days of results.`,
  },
  {
    path: '/practice',
    title: `Practice Rounds and Past Puzzles · ${GAME_NAME}`,
    description: `Replay any previous ${GAME_NAME} puzzle or play random rounds by sport. Free practice that never touches your streak or the leaderboard.`,
  },
  {
    path: '/groups',
    title: `Play With Friends: Private Groups · ${GAME_NAME}`,
    description: `Create a private ${GAME_NAME} group, share the invite link and compare daily scores with up to ${GROUP_MAX_MEMBERS} friends. Free, no account needed.`,
  },
  {
    path: '/privacy',
    title: `Privacy · ${GAME_NAME}`,
    description: `What ${GAME_NAME} stores and why: an anonymous player ID, an optional display name, and analytics only with your consent where the law requires it.`,
  },
  {
    path: '/terms',
    title: `Terms · ${GAME_NAME}`,
    description: `The short terms for playing ${GAME_NAME}: fair play, display names, content and availability.`,
  },
  // In-game and personal screens: shells only, kept out of search results.
  { path: '/play', title: `Play · ${GAME_NAME}`, description: HOME_DESCRIPTION, noindex: true },
  {
    path: '/results',
    title: `Your results · ${GAME_NAME}`,
    description: HOME_DESCRIPTION,
    noindex: true,
  },
  {
    path: '/stats',
    title: `Your stats · ${GAME_NAME}`,
    description: HOME_DESCRIPTION,
    noindex: true,
  },
];

const byPath = (path: string): RouteMeta => ROUTES.find((r) => r.path === path)!;

/** Metadata for any app path: exact routes first, then the dynamic ones (all noindex). */
export function routeMeta(path: string): RouteMeta {
  const exact = ROUTES.find((r) => r.path === path);
  if (exact) return exact;
  if (path === '/how-it-works') return byPath('/how-to-play');
  if (path.startsWith('/play')) return { ...byPath('/play'), path };
  if (path.startsWith('/challenge/')) {
    return {
      path,
      title: `You’ve been challenged · ${GAME_NAME}`,
      description: HOME_DESCRIPTION,
      noindex: true,
    };
  }
  if (path.startsWith('/groups/') || path.startsWith('/join/')) {
    return { path, title: `Group · ${GAME_NAME}`, description: HOME_DESCRIPTION, noindex: true };
  }
  if (path === '/admin') {
    return { path, title: `Editor · ${GAME_NAME}`, description: HOME_DESCRIPTION, noindex: true };
  }
  return {
    path,
    title: `Page not found · ${GAME_NAME}`,
    description: HOME_DESCRIPTION,
    noindex: true,
  };
}
